/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {ThunkAction} from 'model/store/thunk'
import {Lead} from 'types/entities/Lead'
import {deleteLead, deleteLeadNote, deleteLeadTask, pushLead, pushLeadNote, pushLeadTask, setLeads, setLeadsTableMeta, setSelectedLead,} from './OpportunitiesReducer'
import RequestAction from 'store/RequestAction'
import {parsePaginatedResponse} from 'utils/api/pagination'
import {LeadTask} from 'types/entities/LeadTask'
import type {LeadNote} from 'types/entities/LeadNote'
import { MassActionRequest, MassActionResponse } from 'types/requests/MassActionRequest'
import { MassActionType } from 'types/enums/specific/MassAction'
import {enrichOpportunityCanton} from 'services/opportunityCantonService'
import {persistOpportunityCanton} from 'services/opportunityCantonPersistence'
import type {CompanyAddress} from 'types/entities/CompanyAddress'
import type {RootState} from 'helpers/store'

const cantonPersister = (getState: () => RootState) => {
    const token = getState().sessionState.session?.access_token
    const sameSession = () => {
        const current = getState()
        if (!token || current.sessionState.session?.access_token !== token) throw new Error('Session changed during canton lookup')
        return current
    }
    const request = <T>(url: string, data?: CompanyAddress, method: 'get' | 'post' = 'get') => {
        sameSession()
        return RequestAction.makeAuthenticatedRequest<T>(sameSession, url, data, method)
    }
    return (lead: Lead, region: string) => persistOpportunityCanton(lead, region, {
        getLead: async id => (await request<{lead: Lead}>(`/admin/leads/detail/${id}`)).lead,
        getAddress: async id => (await request<{address: CompanyAddress}>(`/admin/companies/address-detail/${id}`)).address,
        saveAddress: async address => (await request<{companyAddress: CompanyAddress}>(
            '/admin/companies/create-address', address, 'post')).companyAddress,
    })
}

export default class OpportunitiesAction {
    static fetchLeadsTablePage = (params: {
        page: number
        per_page: number
        view?: string | 'all'
        search?: any
        sorting?: { id: string; desc: boolean }[]
        sort_by?: string | null
        sort?: 'asc' | 'desc' | null
    }): ThunkAction<
        Promise<{
            data: Lead[]
            current_page: number
            per_page: number
            total: number
        }>
    > => {
        return async (
            dispatch,
            getState
        ): Promise<{
            data: Lead[]
            current_page: number
            per_page: number
            total: number
        }> => {
            const url = `/admin/leads/table-opportunities`

            const response: unknown = await RequestAction.makeAuthenticatedRequest(
                getState,
                url,
                {
                    page: params.page,
                    per_page: params.per_page,
                    view: params.view && params.view !== 'all' ? params.view : undefined,
                    search: params.search,
                    sorting: params.sorting && params.sorting.length > 0 ? params.sorting : undefined,
                },
                'post'
            )
            try {
                const parsedResponse = parsePaginatedResponse<Lead>(response, 'leads')

                dispatch(setLeads(parsedResponse.data))
                dispatch(setLeadsTableMeta({
                    currentPage: parsedResponse.current_page,
                    perPage: parsedResponse.per_page,
                    total: parsedResponse.total,
                    sortBy: params.sort_by ?? params.sorting?.[0]?.id ?? null,
                    sort: params.sort ?? (params.sorting?.length ? (params.sorting[0].desc ? 'desc' : 'asc') : null),
                    search: params.search ?? null,
                }))

                return parsedResponse
            } catch {
                return Promise.reject(response)
            }
        }
    }

    static fetchExportOpportunitiesXlsx = (params: {
        view?: string | 'all'
        search?: any
        sorting?: { id: string; desc: boolean }[]
        sort_by?: string | null
        sort?: 'asc' | 'desc' | null
        export?: 'xlsx' | 'csv' | 'pdf'
        [key: string]: any
    }): ThunkAction<Promise<string>> => {
        return async (dispatch, getState): Promise<string> => {
            const url = `/admin/export/opportunities`

            const response: { url: string } = await RequestAction.makeAuthenticatedRequest(
                getState,
                url,
                {
                    ...params,
                    page: params.page ?? 1,
                    per_page: params.per_page ?? 50,
                    view: params.view && params.view !== 'all' ? params.view : undefined,
                    search: params.search,
                    sorting: params.sorting && params.sorting.length > 0 ? params.sorting : undefined,
                    export: params.export ?? 'xlsx',
                },
                'post',
                'json'
            )

            return response.url
        }
    }

    static fetchCreateOrUpdateLead = (data: Partial<Lead>): ThunkAction<Promise<Lead>> => {
        return async (dispatch, getState): Promise<Lead> => {
            const response: any = await RequestAction.makeAuthenticatedRequest(
                getState,
                '/admin/leads/create',
                { ...data }
            )
            const lead: Lead | undefined = response.lead
            if (lead) {
                // Table list only — callers that own the detail form merge omitted fields
                // (created_date, nested company.origin, …) before setSelectedLead.
                dispatch(pushLead(lead, !data.id))
                return lead
            }
            return Promise.reject(response)
        }
    }

    static fetchMassCreateOrUpdateLeads = (
        items: Partial<Lead>[],
        sourceIds: number[] = []
    ): ThunkAction<Promise<Lead[]>> => {
        return async (dispatch, getState): Promise<Lead[]> => {
            const ids =
                sourceIds.length > 0
                    ? sourceIds
                    : items
                          .map((item) => Number(item.id))
                          .filter((id) => Number.isFinite(id))

            const params = {
                items: items.map((source) => {
                    const item = {...source}
                    delete item.id
                    return item
                }),
            }

            const response: { leads?: Lead[] } = await RequestAction.makeAuthenticatedRequest(
                getState,
                '/admin/leads/mass-actions',
                {
                    ids,
                    action: MassActionType.Create,
                    params,
                },
                'post'
            )
            const leads: Lead[] = response?.leads ?? []
            leads.forEach((lead) => dispatch(pushLead(lead)))
            return leads
        }
    }

    static fetchMassAction = (
        payload: MassActionRequest
    ): ThunkAction<Promise<MassActionResponse>> => {
        return async (_dispatch, getState): Promise<MassActionResponse> => {
            return await RequestAction.makeAuthenticatedRequest(
                getState,
                '/admin/leads/mass-actions',
                payload,
                'post'
            )
        }
    }

    static fetchLeadById = (lead: Lead): ThunkAction<Promise<Lead>> => {
        return async (dispatch, getState): Promise<Lead> => {
            const response: { lead: Lead } = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/leads/detail/${lead.id}`,
                undefined,
                'get'
            )
            if (response?.lead) {
                const enrichedLead = await enrichOpportunityCanton(response.lead, {persist: cantonPersister(getState)})
                dispatch(setSelectedLead(enrichedLead))
                return enrichedLead
            }
            return Promise.reject(response as any)
        }
    }

    static fetchUpdateLead = (data: Partial<Lead>): ThunkAction<Promise<void>> => {
        return async (dispatch, getState) => {
            const response = await RequestAction.makeAuthenticatedRequest<{
                lead: Lead
            }>(getState, `/admin/leads/${data.id}`, data, 'post')
            dispatch(pushLead(response.lead))
        }
    }

    static fetchDeleteLead = (lead: Lead): ThunkAction<Promise<void>> => {
        return async (dispatch, getState): Promise<void> => {
            const response: { success: boolean } = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/leads/delete/${lead.id}`,
                undefined,
                'delete'
            )
            if (response?.success) {
                dispatch(deleteLead(lead.id))
                return Promise.resolve()
            }
            return Promise.reject(response as any)
        }
    }

    static setSelectedLead = (lead: Lead | null) => setSelectedLead(lead)

    static pushLead = (lead: Lead) => pushLead(lead)

    static fetchCreateLeadNote = (data: any): ThunkAction<Promise<any>> => {
        return async (dispatch, getState): Promise<any> => {
            const response: any = await RequestAction.makeAuthenticatedRequest(
                getState,
                '/admin/leads/create-note',
                data
            )
            const leadNote: LeadNote | undefined = response.note
            if (leadNote) {
                dispatch(pushLeadNote(leadNote))
                return response
            }

            return response
        }
    }

    static fetchCreateLeadTask = (data: any): ThunkAction<Promise<any>> => {
        return async (dispatch, getState): Promise<any> => {
            const response: any = await RequestAction.makeAuthenticatedRequest(
                getState,
                '/admin/leads/create-task',
                data
            )
            const leadTask: LeadTask | undefined = response.task
            if (leadTask) {
                dispatch(pushLeadTask(leadTask))
                return response
            }

            return response
        }
    }

    static resetSelectedLead = () => setSelectedLead(null)

    static fetchDeleteLeadNote = (note: LeadNote): ThunkAction<Promise<any>> => {
        return async (dispatch, getState): Promise<any> => {
            const response: any = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/leads/delete-note/${note.id}`,
                undefined,
                'delete'
            )
            dispatch(deleteLeadNote(note))
            return response
        }
    }

    static fetchDeleteLeadTask = (task: LeadTask): ThunkAction<Promise<any>> => {
        return async (dispatch, getState): Promise<any> => {
            const response: any = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/leads/delete-task/${task.id}`,
                undefined,
                'delete'
            )

            dispatch(deleteLeadTask(task))
            return response
        }
    }
}
